import { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { Button } from './ui/button';
import { useWalletStore } from '../../store/walletStore';
import { WalletTransaction as WalletTransactionType } from '../../types';
import { BalanceIcon } from './icons/BalanceIcon';
import { formatBalance } from '../../utils/balanceFormatter';

interface WalletViewProps {
  onRechargeClick: (amount: number) => void;
  onTransactionClick: (sessionId: string) => void;
  isProcessingPayment?: boolean;
}

export function WalletView({ onRechargeClick, onTransactionClick, isProcessingPayment = false }: WalletViewProps) {
  const [customAmount, setCustomAmount] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  // Get data from wallet store
  const {
    wallet,
    isLoading,
    fetchBalance,
  } = useWalletStore();

  // Fetch balance on component mount
  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  const currentBalance = wallet?.balance ?? 0;
  const presetAmounts = [100, 200, 500, 1000, 2000, 5000];

  const handlePresetClick = (amount: number) => {
    onRechargeClick(amount);
  };

  const handleCustomRecharge = () => {
    const amount = parseFloat(customAmount);
    if (amount && amount > 0) {
      onRechargeClick(amount);
      setCustomAmount('');
      setShowCustomInput(false);
    }
  };

  return (
    <div className="h-full bg-gray-50 dark:bg-gray-900 overflow-auto pb-24 transition-colors">
      <div className="px-4 py-6">
        {/* Page Title with Logo */}
        <div className="mb-6 flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
            <svg width="40" height="40" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="50" cy="50" r="45" stroke="#8bc43e" strokeWidth="7"/>
              <rect x="32" y="30" width="10" height="20" rx="1" fill="#8bc43e"/>
              <rect x="58" y="30" width="10" height="20" rx="1" fill="#8bc43e"/>
              <rect x="41" y="58" width="18" height="12" rx="1" fill="#8bc43e"/>
            </svg>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">My Wallet</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Your charging balance</p>
          </div>
        </div>

        {/* Wallet Balance Card */}
        <div className="relative mb-6 overflow-hidden rounded-3xl bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-6 shadow-2xl">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-40 h-40 bg-amber-400 rounded-full blur-3xl opacity-20"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-amber-400 rounded-full blur-3xl opacity-10"></div>

          <div className="relative z-10">
            {/* Wallet Icon */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-xl flex items-center justify-center border border-white/20">
                  <BalanceIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-white/70 text-sm">Balance (₹)</p>
                </div>
              </div>
              <div className="w-10 h-10 bg-white/5 backdrop-blur-md rounded-lg flex items-center justify-center border border-white/10">
                <div className="w-2 h-2 bg-[#8bc43e] rounded-full animate-pulse"></div>
              </div>
            </div>

            {/* Balance Amount */}
            <div className="mb-8">
              <div className="flex items-baseline gap-2">
                {isLoading && !wallet ? (
                  <div className="animate-pulse">
                    <div className="h-12 w-40 bg-white/20 rounded-lg"></div>
                  </div>
                ) : (
                  <span className="text-5xl font-bold text-white balance-shimmer flex items-center gap-2">
                    <BalanceIcon className="w-9 h-9" /> {formatBalance(currentBalance)}
                  </span>
                )}
              </div>
              <p className="text-white/50 text-sm mt-2">Last updated: Just now</p>
              {/* Wallet Status Badge */}
              {wallet?.status && (
                <div className="flex items-center gap-2 mt-2">
                  <div className={`w-2 h-2 rounded-full ${
                    wallet.status === 'active' ? 'bg-green-400' : 'bg-red-400'
                  }`}></div>
                  <span className="text-white/60 text-xs uppercase tracking-wider">
                    {wallet.status}
                  </span>
                </div>
              )}
            </div>

            {/* Card-like details */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/50 text-xs mb-1">Wallet ID</p>
                <p className="text-white/90 text-sm font-mono">
                  **** **** {wallet?.id ? wallet.id.slice(-4) : '----'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-white/50 text-xs mb-1">Active Since</p>
                <p className="text-white/90 text-sm">
                  {wallet?.createdAt
                    ? new Date(wallet.createdAt).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })
                    : '---'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Recharge Section */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 mb-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 dark:text-white">Recharge</h3>
            <Plus className="w-5 h-5 text-[#8bc43e]" />
          </div>

          {/* Preset Amounts */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            {presetAmounts.map((amount) => (
              <button
                key={amount}
                onClick={() => handlePresetClick(amount)}
                disabled={isProcessingPayment}
                className="bg-gray-50 dark:bg-gray-700 hover:bg-[#8bc43e] hover:text-white border-2 border-gray-200 dark:border-gray-600 hover:border-[#8bc43e] rounded-xl py-3 px-4 font-semibold text-gray-900 dark:text-white transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-gray-50 disabled:dark:hover:bg-gray-700"
              >
                ₹{amount}
              </button>
            ))}
          </div>

          {/* Custom Amount Toggle */}
          {!showCustomInput ? (
            <button
              onClick={() => setShowCustomInput(true)}
              disabled={isProcessingPayment}
              className="w-full bg-gradient-to-r from-[#8bc43e] to-[#6fa02e] text-white font-semibold py-3 rounded-xl hover:shadow-lg transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isProcessingPayment ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                'Enter Amount'
              )}
            </button>
          ) : (
            <div className="space-y-3">
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
                  <BalanceIcon className="w-5 h-5" />
                </span>
                <input
                  type="number"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  placeholder="Enter amount"
                  disabled={isProcessingPayment}
                  className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#8bc43e] focus:border-transparent transition-all text-gray-900 dark:text-white disabled:opacity-50"
                  min="1"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => {
                    setShowCustomInput(false);
                    setCustomAmount('');
                  }}
                  disabled={isProcessingPayment}
                  className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold py-3 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCustomRecharge}
                  disabled={!customAmount || parseFloat(customAmount) <= 0 || isProcessingPayment}
                  className="bg-gradient-to-r from-[#8bc43e] to-[#6fa02e] text-white font-semibold py-3 rounded-xl hover:shadow-lg transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessingPayment ? 'Processing...' : 'Recharge'}
                </button>
              </div>
            </div>
          )}
        </div>

      </div>

      {/* Shimmer Animation */}
      <style>{`
        @keyframes balance-shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        .balance-shimmer {
          background: linear-gradient(90deg, currentColor 0%, rgba(251,191,36,0.8) 50%, currentColor 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: balance-shimmer 3s linear infinite;
        }
      `}</style>
    </div>
  );
}
